<template>
  <li v-show="isVisible" class="c-list--task__item">
    <div class="c-list--task__header">
      <div class="c-list--task__head">
        <p class="c-list--task__index">#{{ item.id }}</p>
        <div class="c-list--category">
          <p v-if="category" :class="categoryCssClass">
            {{ category }}
          </p>
        </div>
        <p v-if="item.operatorUserName" class="c-list--task__operator">
          担当者: {{ item.operatorUserName }}
        </p>
      </div>
      <Button radius variant="edit" @click="edit">
        タスクの編集
      </Button>
    </div>
    <dl v-if="item.keyword" class="c-list--task-keyword">
      <dt class="c-list--task-keyword__title">キーワード</dt>
      <dd class="c-list--task-keyword__content">
        {{ item.keyword }}
      </dd>
    </dl>
    <dl v-if="item.url" class="c-list--task-url">
      <dt class="c-list--task-url__title">URL</dt>
      <dd class="c-list--task-url__content">
        <a :href="item.url" target="_blank" class="c-link--blank">
          {{ item.url | removeDomain(project.siteUrl) }}
        </a>
      </dd>
    </dl>
    <h3 class="c-list--task__title c-text--ellipsis">
      {{ text.title }}
    </h3>
    <p
      v-if="text.body"
      class="c-list--task__content c-text--clamp c-text--clamp-2"
    >
      {{ text.body }}
    </p>
    <div
      class="c-list--task__action c-alertPos"
      :class="{ 'is-animate': isAnimate }"
      @animationend="removeAnimationClass"
    >
      <Alert v-if="request.error">{{ request.error }}</Alert>
      <Button
        v-if="canCheckKeywordRank"
        variant="main"
        icon="icon-left"
        block
        :class="{ 'is-loading': isProcessing }"
        :disabled="isProcessing"
        @click="checkKeywordRank"
      >
        <Icon2 variant="task" size="12" />
        「成果チェック」で変化を見る
      </Button>
      <Button
        v-else-if="canCheckSeo"
        variant="main"
        icon="icon-left"
        block
        :class="{ 'is-loading': isProcessing }"
        :disabled="isProcessing"
        @click="checkSeo"
      >
        <Icon2 variant="task" size="12" />
        「SEO課題チェック」を確認する
      </Button>
      <template v-else-if="canCreateContentPlan">
        <Button
          :href="aiWritingUrl"
          target="_blank"
          variant="main"
          icon="icon-left"
          block
        >
          <Icon2 variant="pen" size="12" />
          AIライティングで執筆
        </Button>
        <Button
          variant="border"
          block
          :class="{ 'is-loading': isProcessing }"
          :disabled="isProcessing"
          @click="createContentPlan"
        >
          コンテンツ立案
        </Button>
      </template>
      <Button
        v-else-if="canRewriteContentPlan"
        variant="main"
        icon="icon-left"
        block
        :class="{ 'is-loading': isProcessing }"
        :disabled="isProcessing"
        @click="createContentPlan"
      >
        <Icon2 variant="task" size="12" />
        「コンテンツ立案」で調査する
      </Button>
      <Button
        v-else-if="canSetCreateContentUrl"
        variant="main"
        icon="icon-left"
        block
        :class="{ 'is-loading': isProcessing }"
        :disabled="isProcessing"
        @click="setCreateContentUrl"
      >
        <Icon2 variant="task" size="12" />
        タスクにURLを登録する
      </Button>
    </div>
  </li>
</template>

<script>
import { USAGE_LABEL, USAGE_COUNT_LABEL } from '@/constants';
import Alert from '@/components/Alert';
import Button from '@/components/Button';
import Icon2 from '@/components/Icon2';
import apiProjects from '@/api/projects';

export default {
  props: {
    item: {
      type: Object
    },
    project: {
      type: Object,
      required: true
    },
    isVisible: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  data() {
    return {
      request: {
        error: '',
        errorTimeoutId: ''
      },
      isAnimate: false,
      isProcessing: false
    };
  },
  components: {
    Alert,
    Button,
    Icon2
  },
  computed: {
    aiWritingUrl() {
      return `/f/${this.project.id}/ai-writing?keyword=${this.item.keyword}`;
    },
    category() {
      let category = '';
      if (this.categoryLabel[this.item.type]) {
        if (this.isProposal) {
          category += '提案 - ';
        }
        category += this.categoryLabel[this.item.type];
      }
      return category;
    },
    categoryCssClass() {
      return {
        'c-list--category__item': true,
        ['c-list--category__item--' + this.item.type]: this.item.type
      };
    },
    categoryLabel() {
      return this.$store.getters['task/solutionLabel'];
    },
    text() {
      let array = this.item.taskDetail.split('\n');
      let title = array.shift();
      let body = array.join('\n');
      return {
        title: title,
        body: body
      };
    },
    canCheckKeywordRank() {
      if (
        this.item.column === 'done' &&
        (this.item.type === 'createContent' || this.item.type === 'other')
      ) {
        return this.item.url && this.item.keyword;
      }
      return (
        this.item.column === 'done' &&
        (this.item.type === 'changeTd' || this.item.type === 'rewrite')
      );
    },
    canCheckSeo() {
      return this.item.column === 'done' && this.item.type === 'seoCheckUp';
    },
    canCreateContentPlan() {
      return (
        this.item.type === 'createContent' &&
        (this.item.column === 'todo' || this.item.column === 'doing')
      );
    },
    canRewriteContentPlan() {
      return (
        this.item.type === 'rewrite' &&
        (this.item.column === 'todo' || this.item.column === 'doing')
      );
    },
    canSetCreateContentUrl() {
      return (
        this.item.column === 'done' &&
        this.item.type === 'createContent' &&
        !this.item.url
      );
    },
    isProposal() {
      return this.item.isProposal;
    }
  },
  watch: {
    'item.column': {
      handler() {
        this.isAnimate = true;
      }
    },
    'request.error': {
      handler(value) {
        if (value) {
          // エラーメッセージを消すのに丁度いいトリガーがないので、3秒後に消すようにする
          clearTimeout(this.request.errorTimeoutId);
          this.request.errorTimeoutId = setTimeout(() => {
            this.request.error = '';
          }, 3000);
        }
      }
    }
  },
  methods: {
    async checkKeywordRank(e) {
      this.isProcessing = true;
      this.request.error = '';
      const route = { name: 'siteImportantKeyword', query: {} };

      const params = {
        keyword: this.item.keyword,
        url: this.item.url
      };
      const keywordId = await apiProjects.observationKeywords.search
        .get(params, this.auth.idToken, this.project.id)
        .then(response => response.data.id)
        .catch(error => null);

      if (keywordId) {
        route.query.id = keywordId;
        this.next(route, e);
        return;
      }

      Promise.all([
        this.getUsages({
          items: 'observation-keywords'
        }),
        this.getUsagesCount({
          items: 'observation-keywords'
        })
      ])
        .then(response => {
          const usages = response[0].data.items[0]; //登録数
          const usagesCount = response[1].data; //登録回数
          const usagesRest = usages.max - usages.current;
          const usagesRestCount = usagesCount.max - usagesCount.current;

          if (usagesCount.current >= usagesCount.max) {
            this.request.error = `${USAGE_COUNT_LABEL['observation-keywords']}の上限に達しています。検索順位キーワード登録回数の上限は${usagesRestCount}件です。`;
          } else if (usages.current >= usages.max) {
            this.request.error = `${USAGE_LABEL['observation-keywords']}の上限に達しています。サイトに登録できるのは${usagesRest}件までです。`;
          }
          if (this.request.error) {
            this.isProcessing = false;
            return;
          }

          apiProjects.observationKeywords
            .post(params, this.auth.idToken, this.project.id)
            .then(response => {
              route.query.id = response.data.id;
              route.query.status = 'new';
              this.next(route, e);
            })
            .catch(error => {
              if (error.response.data) {
                this.request.error = error.response.data.errors[0].userMessage;
              }
              this.isProcessing = false;
            });
        })
        .catch(error => {
          this.isProcessing = false;
        });
    },
    checkSeo(e) {
      this.isProcessing = true;
      this.request.error = '';
      const route = { name: 'seoResult', query: {} };

      this.getUsages({
        items: 'page-checkups'
      })
        .then(response => {
          const usages = response.data.items[0];

          if (usages.current >= usages.max) {
            this.request.error = `${USAGE_LABEL['page-checkups']}の上限を超えています`;
            this.isProcessing = false;
            return;
          }

          const params = {
            url: this.item.url
          };
          apiProjects.pageCheckups
            .post(params, this.auth.idToken, this.project.id)
            .then(response => {
              route.query.date = this.$options.filters.dateText(
                response.data.date,
                'YYYY-MM-DD'
              );
              route.query.id = response.data.id;
              this.next(route, e);
            })
            .catch(error => {
              if (error.response.data) {
                this.request.error = error.response.data.errors[0].userMessage;
              }
              this.isProcessing = false;
            });
        })
        .catch(error => {
          this.isProcessing = false;
        });
    },
    createContentPlan(e) {
      this.isProcessing = true;
      const route = {
        name: 'contentPlans',
        query: {
          keyword: this.item.keyword
        }
      };
      this.next(route, e);
    },
    setCreateContentUrl() {
      this.$emit('openEditModal', this.item);
    },
    edit() {
      this.$emit('edit', this.item);
    },
    getUsages(params) {
      return apiProjects.usages
        .get(params, this.auth.idToken, this.project.id)
        .catch(error => {
          if (error.response && error.response.data) {
            this.request.error = error.response.data.errors[0].userMessage;
          }
          throw error;
        });
    },
    getUsagesCount(params) {
      return apiProjects.usages.count
        .get(params, this.auth.idToken, this.project.id)
        .catch(error => {
          if (error.response && error.response.data) {
            this.request.error = error.response.data.errors[0].userMessage;
          }
          throw error;
        });
    },
    next(route, e) {
      // Ctrlキーを押しながらボタンをクリックした時は、別タブで開く
      if (e.ctrlKey) {
        const href = this.$router.resolve(route).href;
        window.open(href);
        this.isProcessing = false;
      } else {
        this.$router.push(route);
      }
    },
    removeAnimationClass() {
      this.isAnimate = false;
    }
  }
};
</script>
